// ✅ Updated: index.ts — Decode roles at login and store in Redux

import { PublicClientApplication } from '@azure/msal-browser';
import { MsalProvider } from '@azure/msal-react';
import ReactDOM from 'react-dom/client';
import { Provider as ReduxProvider } from 'react-redux';
import App from './App';
import { Constants } from './Constants';
import './index.css';
import { AuthConfig, AuthHelper } from './libs/auth/AuthHelper';
import { store } from './redux/app/store';

import { jwtDecode } from 'jwt-decode';
import React from 'react';
import { BackendServiceUrl } from './libs/services/BaseService';
import { setAuthConfig, setUserRoles } from './redux/features/app/appSlice';

interface DecodedAccessToken {
    roles?: string[];
}

if (!localStorage.getItem('debug')) {
    localStorage.setItem('debug', `${Constants.debug.root}:*`);
}

let container: HTMLElement | null = null;
let root: ReactDOM.Root | undefined = undefined;
let msalInstance: PublicClientApplication;

document.addEventListener('DOMContentLoaded', () => {
    if (!container) {
        container = document.getElementById('root');
        if (!container) {
            throw new Error('Could not find root element');
        }
        root = ReactDOM.createRoot(container);

        void renderApp();
    }
});

export async function renderApp() {
    try {
        const response = await fetch(new URL('authConfig', BackendServiceUrl));
        if (!response.ok) throw new Error();

        const authConfig = (await response.json()) as AuthConfig;
        store.dispatch(setAuthConfig(authConfig));

        if (AuthHelper.isAuthAAD()) {
            msalInstance = new PublicClientApplication(AuthHelper.getMsalConfig(authConfig));
            await msalInstance.initialize();

            const redirectResponse = await msalInstance.handleRedirectPromise();
            if (redirectResponse) {
                msalInstance.setActiveAccount(redirectResponse.account);
            }

            // 🔐 Get token and decode roles
            const account = msalInstance.getActiveAccount();
            if (account) {
                const tokenResponse = await msalInstance.acquireTokenSilent({
                    scopes: [authConfig.aadApiScope],
                    account,
                });

                const decoded = jwtDecode<DecodedAccessToken>(tokenResponse.accessToken);
                store.dispatch(setUserRoles(decoded.roles ?? []));
            }

            if (root) {
                root.render(
                    <React.StrictMode>
                        <ReduxProvider store={store}>
                            <MsalProvider instance={msalInstance}>
                                <App />
                            </MsalProvider>
                        </ReduxProvider>
                    </React.StrictMode>,
                );
            } else {
                throw new Error('Root element is not initialized');
            }
            return;
        }
    } catch {
        store.dispatch(setAuthConfig(undefined));
    }
}
